import React, { useState } from 'react';
import {
  Boxes, Briefcase, FileText, Folder, Handshake, Home, Layers, LineChart,
  Package, Phone, Plus, Trash2, Users, X,
} from 'lucide-react';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import {
  useDocumentosCliente,
  useExcluirDocumento,
  useSummaryCliente,
  useUploadDocumento,
} from '../hooks';
import { downloadDocumentoUrl } from '../api';
import type { ClienteDocumento, ClienteSummary } from '../api';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { TextInput } from '../../../shared/components/TextInput';

interface Props {
  clientId: string;
}

type TabKey =
  | 'ativos' | 'projetos' | 'custodiarios' | 'documentos'
  | 'contatos' | 'contratos' | 'propostas' | 'oportunidades' | 'os';

interface TabDef {
  key: TabKey;
  label: string;
  icon: React.ReactNode;
  counter: (s: ClienteSummary) => number;
  /** Spec dedicada quando essa aba for entregue (mensagem no placeholder). */
  futureSpec?: string;
  functional: boolean;
}

const TABS: TabDef[] = [
  { key: 'ativos', label: 'Ativos', icon: <Package size={16} />, counter: (s) => s.contadores.ativos, functional: true },
  { key: 'projetos', label: 'Projetos', icon: <Layers size={16} />, counter: (s) => s.contadores.projetos, functional: true },
  { key: 'custodiarios', label: 'Custodiários', icon: <Users size={16} />, counter: (s) => s.contadores.custodiarios, functional: true },
  { key: 'documentos', label: 'Documentos', icon: <Folder size={16} />, counter: (s) => s.contadores.documentos, functional: true },
  { key: 'contatos', label: 'Contatos', icon: <Phone size={16} />, counter: (s) => s.contadores.contatos, futureSpec: 'Spec 02 — Cadastro de Contatos', functional: false },
  { key: 'contratos', label: 'Contratos', icon: <FileText size={16} />, counter: (s) => s.contadores.contratos, futureSpec: 'Spec 06 — Gestão de Contratos Comerciais', functional: false },
  { key: 'propostas', label: 'Propostas', icon: <Handshake size={16} />, counter: (s) => s.contadores.propostas, futureSpec: 'Spec 04 — Cadastro de Propostas Comerciais', functional: false },
  { key: 'oportunidades', label: 'Oportunidades', icon: <LineChart size={16} />, counter: (s) => s.contadores.oportunidades, futureSpec: 'Spec 08 — Cadastro de Oportunidades', functional: false },
  { key: 'os', label: 'OS', icon: <Briefcase size={16} />, counter: (s) => s.contadores.os, futureSpec: 'Spec 22 — Integração Ordem de Serviço', functional: false },
];

/**
 * Painel Visão 360º do cliente (SPEC 01 Fase 4 — RN-COM-014 + 015).
 *
 * Card de contadores no topo + tabs por vínculo. Abas com dados reais
 * (Ativos, Projetos, Custodiários) linkam para as telas dedicadas com
 * filtro pré-aplicado; Documentos tem CRUD inline; demais abas mostram
 * placeholder indicando a Spec que vai entregar.
 */
export const ClienteVisao360: React.FC<Props> = ({ clientId }) => {
  const summary = useSummaryCliente(clientId);
  const [tab, setTab] = useState<TabKey>('ativos');

  if (summary.isLoading) {
    return <div className="text-sm text-gray-500">Carregando visão 360º…</div>;
  }
  if (summary.isError || !summary.data) {
    return <div className="text-sm text-red-700">Erro ao carregar visão 360º.</div>;
  }
  const s = summary.data;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Boxes size={18} className="text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-900">Visão 360º</h3>
      </div>

      {/* Contadores */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
        {TABS.map((t) => {
          const n = t.counter(s);
          const isActive = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`flex flex-col items-start gap-1 rounded-md border p-3 text-left transition-colors ${
                isActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              } ${!t.functional && n === 0 ? 'opacity-70' : ''}`}
            >
              <div className="flex items-center gap-2 text-xs text-gray-500">
                {t.icon}
                {t.label}
              </div>
              <div className="text-xl font-semibold text-gray-900">{n}</div>
              {!t.functional && <div className="text-[10px] text-gray-400">em breve</div>}
            </button>
          );
        })}
      </div>

      {/* Aba selecionada */}
      <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
        {tab === 'ativos' && <TabExternalLink label="Ativos" clientId={clientId} to={`/ativos?tab=lista&clientId=${clientId}`} count={s.contadores.ativos} icon={<Package size={16} />} />}
        {tab === 'projetos' && <TabExternalLink label="Projetos" clientId={clientId} to={null} count={s.contadores.projetos} icon={<Layers size={16} />} note="Módulo de projetos ainda não tem tela dedicada. Use a API GET /api/projects?clientId=... para consultar." />}
        {tab === 'custodiarios' && <TabExternalLink label="Custodiários" clientId={clientId} to="/custodiarios" count={s.contadores.custodiarios} icon={<Users size={16} />} note='Filtre por Tipo="Cliente" para ver os custodiários vinculados.' />}
        {tab === 'documentos' && <DocumentosTab clientId={clientId} />}
        {tab !== 'ativos' && tab !== 'projetos' && tab !== 'custodiarios' && tab !== 'documentos' && (
          <PlaceholderTab tab={TABS.find((t) => t.key === tab)!} />
        )}
      </div>
    </div>
  );
};

// ============================================================
// Sub-componentes
// ============================================================

interface TabExternalLinkProps {
  label: string;
  clientId: string;
  to: string | null;
  count: number;
  icon: React.ReactNode;
  note?: string;
}

const TabExternalLink: React.FC<TabExternalLinkProps> = ({ label, to, count, icon, note }) => (
  <div className="space-y-2 text-sm text-gray-700">
    <div className="flex items-center gap-2 font-medium">
      {icon} {count} {count === 1 ? 'registro' : 'registros'} de {label.toLowerCase()}
    </div>
    {note && <p className="text-gray-600">{note}</p>}
    {to && (
      <a
        href={to}
        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
      >
        Abrir listagem de {label} →
      </a>
    )}
  </div>
);

const PlaceholderTab: React.FC<{ tab: TabDef }> = ({ tab }) => (
  <div className="space-y-2 text-sm">
    <div className="flex items-center gap-2 font-medium text-gray-700">
      {tab.icon} {tab.label}
    </div>
    <p className="text-gray-600">
      Módulo ainda não disponível — será entregue pela{' '}
      <strong className="text-gray-800">{tab.futureSpec}</strong>. Quando implementado, esta
      aba passa a mostrar os registros vinculados ao cliente automaticamente
      (contrato de leitura RN-COM-013).
    </p>
  </div>
);

// ============================================================
// Documentos
// ============================================================

const DocumentosTab: React.FC<{ clientId: string }> = ({ clientId }) => {
  const query = useDocumentosCliente(clientId);
  const upload = useUploadDocumento(clientId);
  const excluir = useExcluirDocumento(clientId);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ClienteDocumento | null>(null);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  const docs: ClienteDocumento[] = query.data?.data ?? [];

  const showFeedback = (kind: 'ok' | 'err', msg: string) => {
    setFeedback({ kind, msg });
    setTimeout(() => setFeedback(null), 4000);
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    try {
      await excluir.mutateAsync(confirmDelete.id);
      showFeedback('ok', 'Documento removido.');
      setConfirmDelete(null);
    } catch (err) {
      showFeedback('err', getFriendlyErrorMessage(err, 'Falha ao remover documento.'));
      setConfirmDelete(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <Folder size={16} /> Documentos institucionais
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus size={14} /> Novo documento
        </button>
      </div>

      {feedback && (
        <div className={`rounded-md border p-3 text-sm ${
          feedback.kind === 'ok'
            ? 'border-green-200 bg-green-50 text-green-700'
            : 'border-red-200 bg-red-50 text-red-700'
        }`}>
          {feedback.msg}
        </div>
      )}

      {query.isLoading ? (
        <div className="text-sm text-gray-500">Carregando documentos…</div>
      ) : docs.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
          Nenhum documento anexado.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Arquivo</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enviado em</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {docs.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm text-gray-700">{d.tipo}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{d.nome}</td>
                  <td className="px-4 py-2 text-xs text-gray-600 font-mono">
                    {d.fileName} · {formatSize(d.fileSize)}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {new Date(d.createdAt).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <a
                        href={downloadDocumentoUrl(clientId, d.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        Baixar
                      </a>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(d)}
                        disabled={excluir.isPending}
                        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                        title="Excluir documento"
                        aria-label={`Excluir documento ${d.nome}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <DocumentoUploadModal
          submitting={upload.isPending}
          onCancel={() => setModalOpen(false)}
          onSubmit={async (payload) => {
            try {
              await upload.mutateAsync(payload);
              showFeedback('ok', 'Documento anexado.');
              setModalOpen(false);
            } catch (err) {
              showFeedback('err', getFriendlyErrorMessage(err, 'Falha ao anexar documento.'));
            }
          }}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Excluir documento?"
        message={
          confirmDelete
            ? `${confirmDelete.nome}\n${confirmDelete.fileName}\n\nEsta ação não pode ser desfeita.`
            : ''
        }
        confirmLabel="Excluir"
        danger
        busy={excluir.isPending}
        onConfirm={performDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
};

interface DocumentoUploadModalProps {
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (payload: { tipo: string; nome: string; expiresAt?: string; file: File }) => Promise<void>;
}

const DocumentoUploadModal: React.FC<DocumentoUploadModalProps> = ({ submitting, onCancel, onSubmit }) => {
  const [tipo, setTipo] = useState('');
  const [nome, setNome] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const inputCls = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!tipo.trim() || !nome.trim()) {
      setError('Tipo e nome são obrigatórios.');
      return;
    }
    if (!file) {
      setError('Selecione um arquivo.');
      return;
    }
    await onSubmit({
      tipo: tipo.trim(),
      nome: nome.trim(),
      expiresAt: expiresAt || undefined,
      file,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <h3 className="text-xl font-bold text-gray-900">Novo documento</h3>
          <button type="button" onClick={onCancel} aria-label="Fechar" className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3 p-6">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Tipo *</span>
            <input
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              placeholder="Ex.: contrato_social, procuracao"
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Nome *</span>
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex.: Contrato Social 2026"
              className={inputCls}
            />
          </label>
          <TextInput
            label="Data de validade (opcional)"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Arquivo *</span>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              accept="application/pdf,image/*"
              className={inputCls}
            />
            <p className="mt-1 text-xs text-gray-500">Máx. 20MB. PDF ou imagem.</p>
          </label>

          {error && <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onCancel} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
              Cancelar
            </button>
            <button type="submit" disabled={submitting} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {submitting ? 'Enviando…' : 'Anexar documento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
